'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { HubConnection } from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { createHubConnection } from '@/lib/signalr';
import { AuthContext } from './auth-provider';

export interface SignalRContextValue {
  connection: HubConnection | null;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
}

export const SignalRContext = createContext<SignalRContextValue>({
  connection: null,
  connectionStatus: 'disconnected',
});

interface SignalRProviderProps {
  children: ReactNode;
}

/**
 * Provider que gerencia a conexao SignalR.
 * Requer o servidor .NET Core 8 (Nosso.Servidor.Core) com hub moderno.
 * Se o servidor nao estiver disponivel, falha silenciosamente
 * e o app usa polling como fallback (via useDocumentStatus).
 */
export function SignalRProvider({ children }: SignalRProviderProps) {
  const auth = useContext(AuthContext);
  const [connectionStatus, setConnectionStatus] = useState<
    'connected' | 'disconnected' | 'reconnecting'
  >('disconnected');
  const connectionRef = useRef<HubConnection | null>(null);

  useEffect(() => {
    const signalrUrl = process.env.NEXT_PUBLIC_SIGNALR_URL;

    // Se nao ha usuario ou SIGNALR_URL nao configurada, nao tenta conectar
    if (!auth?.user?.id || !signalrUrl) {
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
        connectionRef.current = null;
        setConnectionStatus('disconnected');
      }
      return;
    }

    const userId = String(auth.user.id);
    const connection = createHubConnection(userId);
    connectionRef.current = connection;

    connection.onreconnecting(() => {
      setConnectionStatus('reconnecting');
    });

    connection.onreconnected(() => {
      setConnectionStatus('connected');
    });

    connection.onclose(() => {
      setConnectionStatus('disconnected');
    });

    connection
      .start()
      .then(() => {
        setConnectionStatus('connected');
      })
      .catch(() => {
        // Falha silenciosa - app usa polling como fallback
        setConnectionStatus('disconnected');
        connectionRef.current = null;
      });

    return () => {
      if (connection.state !== HubConnectionState.Disconnected) {
        connection.stop().catch(() => {});
      }
      connectionRef.current = null;
      setConnectionStatus('disconnected');
    };
  }, [auth?.user?.id]);

  const value = useMemo<SignalRContextValue>(
    () => ({
      connection: connectionRef.current,
      connectionStatus,
    }),
    [connectionStatus]
  );

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
}

export function useSignalR() {
  return useContext(SignalRContext);
}
